import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, newClientMsgId, uploadAttachment, type CannedResponse, type Message, type OperatorInfo } from "../../api";
import type { AgentSocket } from "../../socket";
import { ClipIcon, SmileIcon } from "../../components/Icons";
import { firstFile, firstImage, precheckFile } from "../../components/Media";
import { useI18n } from "../../lib/i18n";
import { EMOJI, insertAt } from "../../lib/emoji";
import { extractMentions, filterTeam, insertMention, mentionQuery, type Mentionable } from "../../lib/mention";

/**
 * Where the operator writes.
 *
 * Two modes — a reply the customer sees, a note only the team sees — with the
 * note mode coloured like a sticky note so nobody sends one by accident. A
 * slash at the start opens the saved replies; an @ in a note calls in a
 * teammate; a pasted or dropped screenshot goes out as an image, and the clip
 * also takes a PDF or a text file. Above an empty box sit the saved replies
 * that fit the customer's last message.
 */

/** Saved replies matching what follows the slash, most used first. */
export function filterCanned(all: CannedResponse[], query: string): CannedResponse[] {
  const q = query.toLocaleLowerCase("tr");
  return all
    .filter((c) => !q || c.shortcut.toLocaleLowerCase("tr").includes(q) || c.title.toLocaleLowerCase("tr").includes(q))
    .sort((a, b) => b.usageCount - a.usageCount || a.shortcut.localeCompare(b.shortcut))
    .slice(0, 8);
}

/**
 * What was being written in each conversation, for as long as the console is
 * open.
 *
 * An operator with four chats going answers them in turns, and used to lose
 * half a reply every time they clicked across to check another one. Kept in
 * memory only: a half-written reply is not something to leave in a browser's
 * storage after sign-out.
 */
export class DraftStore {
  private readonly drafts = new Map<number, { text: string; note: boolean }>();

  get(conversationId: number): { text: string; note: boolean } {
    return this.drafts.get(conversationId) ?? { text: "", note: false };
  }

  set(conversationId: number, text: string, note: boolean): void {
    if (text) this.drafts.set(conversationId, { text, note });
    else this.drafts.delete(conversationId);
    // A long shift opens many conversations; the oldest unsent draft is the
    // one least likely to still be wanted.
    if (this.drafts.size > 100) this.drafts.delete(this.drafts.keys().next().value!);
  }

  clear(): void {
    this.drafts.clear();
  }
}

export const drafts = new DraftStore();

export function Composer({
  conversationId,
  self,
  canned,
  suggestions,
  team,
  socket,
  onMessage,
  onRemove,
  onError,
}: {
  conversationId: number;
  self: OperatorInfo;
  canned: CannedResponse[];
  /** Saved replies that match what the customer last wrote. */
  suggestions: CannedResponse[];
  team: Mentionable[];
  socket: AgentSocket;
  onMessage: (message: Message) => void;
  onRemove: (clientMsgId: string) => void;
  onError: (text: string) => void;
}): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.composer;
  const [draft, setDraftState] = useState(() => drafts.get(conversationId).text);
  const [caret, setCaret] = useState(0);
  const [note, setNoteState] = useState(() => drafts.get(conversationId).note);
  const [dragOver, setDragOver] = useState(false);
  const [pick, setPick] = useState(0);
  const [pickerClosed, setPickerClosed] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const input = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const typingSent = useRef(0);
  /** The conversation on screen now, for a send that answers after the operator moved on. */
  const onScreen = useRef(conversationId);
  onScreen.current = conversationId;

  // Written through these rather than from an effect: an effect keyed on the
  // conversation would run once with the *previous* conversation's text still
  // in state, and file it under the new one.
  const setDraft = (text: string): void => {
    setDraftState(text);
    drafts.set(conversationId, text, note);
  };
  const setNote = (next: boolean): void => {
    setNoteState(next);
    drafts.set(conversationId, draft, next);
  };

  // Each conversation opens on whatever was left unsent in it.
  useEffect(() => {
    const saved = drafts.get(conversationId);
    setDraftState(saved.text);
    setNoteState(saved.note);
    setEmojiOpen(false);
    input.current?.focus();
  }, [conversationId]);

  const slash = /^\/(\S*)$/.exec(draft);
  const matches = useMemo(() => (slash ? filterCanned(canned, slash[1] ?? "") : []), [canned, slash?.[1]]);
  const pickerOpen = !!slash && !pickerClosed;

  // Mentions only exist in notes: a reply goes to the customer, who has no use for our names.
  const mention = note ? mentionQuery(draft, caret) : null;
  const people = useMemo(() => (mention ? filterTeam(team, mention.query, self.id) : []), [team, mention?.query, self.id]);
  const mentionOpen = !!mention && !pickerClosed;

  useEffect(() => {
    setPick(0);
    setPickerClosed(false);
  }, [slash?.[1], mention?.query]);

  function placeholder(kind: Message["kind"], body: string, clientMsgId: string): Message {
    return {
      id: -Date.now(),
      conversationId,
      kind,
      operatorId: self.id,
      body,
      links: [],
      clientMsgId,
      createdAt: new Date().toISOString(),
      operator: self,
      attachments: [],
      pending: true,
    };
  }

  async function send(): Promise<void> {
    const body = draft.trim();
    if (!body) return;
    setDraft("");
    setEmojiOpen(false);
    const clientMsgId = newClientMsgId();
    const sentIn = conversationId;
    onMessage(placeholder(note ? "note" : "operator", body, clientMsgId));
    const mentions = note ? extractMentions(body, team.filter((m) => m.id !== self.id)) : [];
    try {
      const res = await api.sendMessage(sentIn, body, clientMsgId, note, mentions);
      onMessage(res.message);
    } catch {
      onRemove(clientMsgId);
      // Handed back to the conversation it was written in — which may no
      // longer be the one on screen — and never over something typed since.
      if (!drafts.get(sentIn).text) drafts.set(sentIn, body, note);
      if (onScreen.current === sentIn) setDraftState((current) => current || body);
      onError(t.sendFailed);
    }
  }

  function sendFile(file: File): void {
    const problem = precheckFile(file);
    if (problem) {
      onError(problem.kind === "type" ? t.fileType : t.fileTooBig(problem.mb));
      return;
    }
    const clientMsgId = newClientMsgId();
    const base = { ...placeholder(note ? "note" : "operator", "", clientMsgId), progress: 0 };
    onMessage(base);
    uploadAttachment(conversationId, file, file.name || "screenshot.png", clientMsgId, note, (progress) =>
      onMessage({ ...base, progress }),
    )
      .then(onMessage)
      .catch(() => {
        onRemove(clientMsgId);
        onError(t.uploadFailed);
      });
  }

  function choose(item: CannedResponse): void {
    setDraft(item.body);
    setPickerClosed(true);
    void api.useCanned(item.id).catch(() => undefined);
    input.current?.focus();
  }

  function placeCaret(at: number): void {
    setCaret(at);
    window.requestAnimationFrame(() => {
      input.current?.focus();
      input.current?.setSelectionRange(at, at);
    });
  }

  function chooseMention(member: Mentionable): void {
    if (!mention) return;
    const next = insertMention(draft, mention.start, caret, member.displayName);
    setDraft(next.text);
    placeCaret(next.caret);
  }

  function chooseEmoji(emoji: string): void {
    const next = insertAt(draft, input.current?.selectionStart ?? caret, emoji);
    setDraft(next.text);
    placeCaret(next.caret);
  }

  function onKeyDown(event: JSX.TargetedKeyboardEvent<HTMLTextAreaElement>): void {
    if (emojiOpen && event.key === "Escape") {
      event.preventDefault();
      setEmojiOpen(false);
      return;
    }
    if (mentionOpen && people.length > 0) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const step = event.key === "ArrowDown" ? 1 : -1;
        setPick((p) => (p + step + people.length) % people.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        chooseMention(people[pick]!);
        return;
      }
    }
    if (mentionOpen && event.key === "Escape") {
      event.preventDefault();
      setPickerClosed(true);
      return;
    }
    if (pickerOpen && matches.length > 0) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const step = event.key === "ArrowDown" ? 1 : -1;
        setPick((p) => (p + step + matches.length) % matches.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        choose(matches[pick]!);
        return;
      }
    }
    if (pickerOpen && event.key === "Escape") {
      event.preventDefault();
      setPickerClosed(true);
      return;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void send();
      return;
    }
    // Throttled: a frame every two seconds is plenty for "typing…".
    const now = Date.now();
    if (!note && now - typingSent.current > 2000) {
      typingSent.current = now;
      socket.send({ t: "typing", data: { conversationId, typing: true } });
    }
  }

  return (
    <div class={`composer${note ? " note" : ""}`}>
      {mentionOpen ? (
        <div class="picker" role="listbox" aria-label={t.mentionLabel}>
          {people.length === 0 ? (
            <div class="none">{t.noMention}</div>
          ) : (
            people.map((member, index) => (
              <button
                key={member.id}
                role="option"
                aria-selected={index === pick}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => chooseMention(member)}
              >
                <span class="k">@</span>
                {member.displayName}
              </button>
            ))
          )}
        </div>
      ) : null}
      {!note && !draft && suggestions.length > 0 ? (
        <div class="suggest" role="group" aria-label={t.suggestions}>
          {suggestions.map((item) => (
            <button key={item.id} class="suggest-chip" title={item.body} onClick={() => choose(item)}>
              <span class="k">/{item.shortcut}</span>
              {item.title}
            </button>
          ))}
        </div>
      ) : null}
      {pickerOpen ? (
        <div class="picker" role="listbox" aria-label={t.cannedLabel}>
          {matches.length === 0 ? (
            <div class="none">{canned.length === 0 ? t.noCanned : t.noCannedMatch}</div>
          ) : (
            matches.map((item, index) => (
              <button
                key={item.id}
                role="option"
                aria-selected={index === pick}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(item)}
              >
                <span class="k">/{item.shortcut}</span>
                {item.title}
                <span class="b">{item.body}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
      {emojiOpen ? (
        <div class="picker emoji-grid" role="group" aria-label={t.emoji}>
          {EMOJI.map((emoji) => (
            // mousedown is swallowed so the textarea keeps its caret, which is
            // where the emoji is going.
            <button key={emoji} onMouseDown={(e) => e.preventDefault()} onClick={() => chooseEmoji(emoji)}>
              {emoji}
            </button>
          ))}
        </div>
      ) : null}

      <div class="modes" role="group" aria-label={t.modes}>
        <button aria-pressed={!note} onClick={() => setNote(false)}>
          {t.reply}
        </button>
        <button class="note" aria-pressed={note} onClick={() => setNote(true)}>
          {t.note}
        </button>
      </div>

      <div
        class={`box${dragOver ? " drop" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = firstFile(e.dataTransfer?.files);
          if (file) sendFile(file);
        }}
      >
        <button class="btn ghost icon" onClick={() => fileInput.current?.click()} aria-label={t.attach} title={t.attach}>
          <ClipIcon size={18} />
        </button>
        <button
          class="btn ghost icon"
          aria-label={t.emoji}
          title={t.emoji}
          aria-expanded={emojiOpen}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setEmojiOpen((open) => !open)}
        >
          <SmileIcon size={18} />
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,.pdf,.txt,.log"
          hidden
          onChange={(e) => {
            const file = e.currentTarget.files?.[0];
            e.currentTarget.value = "";
            if (file) sendFile(file);
          }}
        />
        <textarea
          ref={input}
          rows={1}
          value={draft}
          aria-label={note ? t.note : t.reply}
          placeholder={note ? t.notePlaceholder : t.replyPlaceholder}
          // The browser's own spell checker, in the language the operator works in.
          spellcheck
          lang={lang}
          onInput={(e) => {
            setDraft(e.currentTarget.value);
            setCaret(e.currentTarget.selectionStart ?? e.currentTarget.value.length);
          }}
          onKeyUp={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
          onClick={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
          onKeyDown={onKeyDown}
          onPaste={(e) => {
            const file = firstImage(e.clipboardData?.items);
            if (file) {
              e.preventDefault();
              sendFile(file);
            }
          }}
        />
        <button class="btn primary" onClick={() => void send()} disabled={!draft.trim()}>
          {note ? t.saveNote : t.send}
        </button>
      </div>
      <div class="foot">
        <span>{t.hintEnter}</span>
        <span>{note ? t.hintMention : t.hintSlash}</span>
      </div>
    </div>
  );
}
